import React, { createContext, useContext, useEffect } from 'react'
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { getSession, setAuthState, signOut as signOutAction } from '@/store/slices/authSlice'
import { auth } from '@/lib/auth'

interface AuthContextType {
  session: Session | null
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
  getUser: () => Promise<{ data: { user: User | null }, error: any }>
  getUserId: () => string | null
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const { session, user, loading } = useAppSelector((state) => state.auth)

  useEffect(() => {
    dispatch(getSession())

    const {
      data: { subscription },
    } = auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      dispatch(setAuthState({ session, user: session?.user ?? null }))
    })

    return () => subscription.unsubscribe()
  }, [dispatch])

  const signOut = async () => {
    await dispatch(signOutAction()).unwrap()
  }

  const getUser = async () => {
    return await auth.getUser()
  }

  const getUserId = () => {
    return user?.id || null
  }

  const value = {
    session,
    user,
    loading,
    signOut,
    getUser,
    getUserId,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}